import dayjs from "dayjs";
import { Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomDatePicker from "../../../../../../../components/CustomDatePicker";
import CustomSelect from "../../../../../../../components/CustomSelect";
import Input from "../../../../../../../components/Input";
import DropZone from "../../../../../../../components/app/DropZone";
import StyledFormModal from "../../../../../../../components/globalModal/StyledModal";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import useSelectedPatient from "../../../../../../../hooks/local-storage/use-selected-patient";
import { DisclosureHost } from "../../../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import { usePatientDocumentCreate } from "../../../../../../../queries/document/use-patient-document-create";
import { usePatientDocumentUpdate } from "../../../../../../../queries/document/use-patient-document-update";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
} from "../../../../../../../utils/utils";
import {
  DOCUMENT_FORMAT_MAPPER,
  DOCUMENT_INITIAL_VALUE,
  DOCUMENT_TAGS,
  DocumentForm,
} from "./document-constant";
import { getStoredRealm } from "@/utils/auth-storage";

interface UpserDiagnosisDrawerProps {
  disclosureHost: DisclosureHost;
}

const UpsertUploadDocumentDrawer = ({
  disclosureHost,
}: UpserDiagnosisDrawerProps) => {
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const [patientData] = useSelectedPatient();
  const { mutateAsync: createDocument } = usePatientDocumentCreate();
  const { mutateAsync: updateDocument } = usePatientDocumentUpdate();

  const formik = useFormik<DocumentForm>({
    initialValues: disclosureHost.data || DOCUMENT_INITIAL_VALUE,
    validationSchema: Yup.object({
      documentTag: Yup.string().trim().required().label("Document Tag"),
      name: Yup.string().trim().required().label("Document"),
      documentDescription: Yup.string()
        .trim()
        .required("Document Title is required"),
      file: Yup.mixed()
        .required("File is required")
        .test(
          "fileType",
          "Unsupported file format. Please upload a valid file.",
          (value: any) => {
            if (!value) return false;
            else if (typeof value === "string" && value.startsWith("https:"))
              return true;
            const supportedFormats = supportedFileTypes;
            return supportedFormats.includes(value.type);
          }
        )
        .label("Document"),
      // documentDate: Yup.string().trim().required().label("Document Date"),
    }),
    onSubmit: submitDocumentHandler,
    enableReinitialize: true,
    validateOnChange: true,
  });

  async function submitDocumentHandler(values?: DocumentForm) {
    const payload: any = {
      patientDocumentId: disclosureHost?.data
        ? disclosureHost?.data?.uuid
        : undefined,
      documentDescription: values?.documentDescription?.trim(),
      documentDate: values?.documentDate,
      file: values?.file,
      documentFormat: values?.documentFormat,
      documentTag: values?.documentTag,
      name: values?.name,
      patientId: patientData?.uuid,
      xTENANTID: getStoredRealm() ?? undefined,
    };

    if (disclosureHost?.data) showToastAndResetForm(updateDocument(payload));
    else showToastAndResetForm(createDocument(payload));
  }

  async function showToastAndResetForm(apiCall: Promise<any>) {
    await apiCall
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));

        queryClient.invalidateQueries({
          queryKey: ["patient-document-list"],
        });

        closeAndReset();
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  }

  function closeAndReset() {
    formik.resetForm();
    disclosureHost.close();
  }

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={closeAndReset}
      dialogTitle={disclosureHost.data ? "Edit Document" : "Upload Document"}
      dialogAction={
        <StyledButtonV2
          label="Upload"
          variant="contained"
          onClick={formik.submitForm}
        />
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "450px",
        },
      }}
    >
      <Grid container spacing={3} pt={2}>
        <Grid item xs={6}>
          <CustomSelect
            required
            label="Document Tag"
            value={formik.values.documentTag}
            placeholder="Select  Tag"
            options={DOCUMENT_TAGS}
            onChange={(v) => formik.setFieldValue("documentTag", v)}
            error={formik.touched?.documentTag && formik.errors?.documentTag}
          />
        </Grid>
        <Grid item xs={6}>
          <CustomDatePicker
            // required
            placeholder="Choose Date"
            name="documentDate"
            value={formik.values.documentDate}
            label="Date"
            handleBlur={formik.handleBlur}
            onChange={(val) => {
              formik.setFieldTouched("documentDate", true);
              formik.setFieldValue(
                "documentDate",
                val ? val?.toISOString() : undefined
              );
            }}
            maxDate={dayjs().endOf("day")}
            // error={formik.touched.documentDate && formik.errors.documentDate}
          />
        </Grid>

        <Grid item xs={6}>
          <Input
            label="Document Title"
            required
            textFieldProps={{
              placeholder: "Enter Title",
              name: "documentDescription",
              value: formik.values.documentDescription,
              onChange: (e) =>
                formik.setFieldValue("documentDescription", e.target.value),
              onBlur: formik.handleBlur,
            }}
            error={
              formik.touched.documentDescription &&
              formik.errors.documentDescription
            }
          />
        </Grid>
        <Grid item xs={12} mt={2}>
          <DropZone
            supportedFile="JPEG, PNG, GIF, PDF, PSD, AI, Word, PPT"
            value={formik.values?.name as any}
            height={"243px"}
            accept="all"
            content="Drag & Drop files Or Browse Files"
            onClick={(file) => {
              const curFile = file?.item(0);

              formik.setFieldValue("file", curFile);
              formik.setFieldValue("name", curFile?.name);

              if (curFile?.type) {
                formik.setFieldValue(
                  "documentFormat",
                  DOCUMENT_FORMAT_MAPPER?.[curFile.type] ?? "OTHER"
                );
              }
              formik.validateForm();
              formik.setFieldTouched("file", !curFile);
            }}
            error={formik.touched.file && formik.errors.file}
          />
        </Grid>
      </Grid>
    </StyledFormModal>
  );
};

export default UpsertUploadDocumentDrawer;
const supportedFileTypes = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "video/mp4",
  "application/pdf",
  "image/vnd.adobe.photoshop", // for PSD
  "application/postscript", // for AI
  "application/msword", // for Word
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // for Word (docx)
  "application/vnd.ms-powerpoint", // for PPT
  "application/vnd.openxmlformats-officedocument.presentationml.presentation", // for PPTX
];
